import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, LogIn, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Logo } from '../components/common/Logo';
import { CompetitionImage } from '../components/competitions/CompetitionImage';
import { NamePrompt } from '../components/common/NamePrompt';
import { api } from '../services/api';
import type { CompetitionInvitePreview } from '../types';

export function JoinCompetitionPage() {
  const navigate = useNavigate();
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const { isAuthenticated, isLoading: authLoading, refreshSubscriber, subscriber } = useAuth();

  const [competition, setCompetition] = useState<CompetitionInvitePreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinStatus, setJoinStatus] = useState<'pending' | 'active' | 'waitlisted' | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Standings hide nameless players — capture a display name before joining.
  const needsName = !subscriber?.name?.trim();

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate(`/login?redirect=/tournaments/join/${inviteCode}`, { replace: true });
      return;
    }
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, inviteCode]);

  const loadPreview = async () => {
    if (!inviteCode) return;
    setIsLoading(true);
    try {
      const response = await api.competitions.invitePreview(inviteCode);
      if (response.success && response.data) {
        if (response.data.isMember) {
          navigate(`/tournaments/${response.data.id}`, { replace: true });
          return;
        }
        setCompetition(response.data);
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!inviteCode) return;
    setIsJoining(true);
    setJoinError(null);
    try {
      const response = await api.competitions.join(inviteCode);
      if (response.success && response.data) {
        setJoinStatus(response.data.status);
        await refreshSubscriber();
        // Confirmed joins (active or queued) go straight to the tournament; only a
        // pending (approval-gated) join lingers on the confirmation screen.
        if (response.data.status === 'active' || response.data.status === 'waitlisted') {
          setTimeout(() => {
            navigate(`/tournaments/${response.data!.competitionId}`, { replace: true });
          }, 1400);
        }
      } else {
        if (response.error?.code === 'ALREADY_MEMBER') {
          const cid = (response as any).data?.competitionId;
          if (cid) navigate(`/tournaments/${cid}`, { replace: true });
          return;
        }
        setJoinError(response.error?.message || 'Erro ao entrar no torneio');
      }
    } catch {
      setJoinError('Erro de conexão');
    } finally {
      setIsJoining(false);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-deep-blue flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-deep-blue flex flex-col items-center justify-center p-6">
        <Logo size="sm" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mt-8 max-w-xs">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-coral/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-coral" />
          </div>
          <h1 className="font-display text-xl font-bold text-white mb-2">Convite não encontrado</h1>
          <p className="text-white/50 text-sm mb-6">
            Este link de convite é inválido ou o torneio não existe mais.
          </p>
          <Button onClick={() => navigate('/tournaments')} fullWidth>
            Ir para Torneios
          </Button>
        </motion.div>
      </div>
    );
  }

  if (joinStatus && competition) {
    const isPending = joinStatus === 'pending';
    const isWaitlisted = joinStatus === 'waitlisted';
    const usesWaitIcon = isPending || isWaitlisted;
    const title = isPending
      ? 'Pedido enviado!'
      : isWaitlisted
        ? 'Você entrou na fila!'
        : 'Inscrição confirmada!';
    const message = isPending
      ? `Aguardando aprovação do organizador de ${competition.name}...`
      : isWaitlisted
        ? `Você entrou na fila de espera de ${competition.name}. O organizador pode te colocar em uma dupla.`
        : `Você entrou em ${competition.name}.`;
    return (
      <div className="min-h-screen bg-deep-blue flex flex-col items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-xs">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
            className={`w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
              usesWaitIcon ? 'bg-sunny/10' : 'bg-turquoise/10'
            }`}
          >
            {usesWaitIcon ? (
              <Clock className="w-10 h-10 text-sunny" />
            ) : (
              <CheckCircle className="w-10 h-10 text-turquoise" />
            )}
          </motion.div>
          <h1 className="font-display text-xl font-bold text-white mb-2">{title}</h1>
          <p className="text-white/50 text-sm">{message}</p>
          {isPending && (
            <Button onClick={() => navigate(`/tournaments/${competition.id}`)} fullWidth className="mt-6">
              Ver torneio
            </Button>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-blue flex flex-col items-center justify-center p-6">
      <Link to="/">
        <Logo size="sm" />
      </Link>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mt-8 max-w-xs w-full">
        {competition && (
          <>
            <div className="flex justify-center mb-4">
              <CompetitionImage imageUrl={competition.imageUrl} size="lg" />
            </div>

            <h1 className="font-display text-2xl font-bold text-white mb-1">{competition.name}</h1>

            {competition.description && (
              <p className="text-white/40 text-sm mb-2 whitespace-pre-line">{competition.description}</p>
            )}

            <p className="text-white/50 text-sm flex items-center justify-center gap-1.5 mb-2">
              <Users className="w-4 h-4" />
              {competition.playerCount} jogador{competition.playerCount !== 1 ? 'es' : ''}
            </p>

            {competition.requiresApproval && (
              <p className="text-xs text-sunny/80 mb-6">Inscrição sujeita à aprovação do organizador</p>
            )}

            {competition.status === 'finished' || competition.status === 'canceled' ? (
              <div className="p-4 rounded-2xl bg-white/10 mb-4 text-center">
                <p className="text-white font-semibold">Torneio encerrado</p>
                <p className="text-white/50 text-xs mt-1">
                  Este torneio já foi finalizado.
                </p>
              </div>
            ) : needsName ? (
              <NamePrompt subtitle={`Esse é o nome que os outros jogadores vão ver em ${competition.name}.`} />
            ) : (
              <>
                {(competition.status === 'group_stage' || competition.status === 'knockout') && (
                  <p className="text-xs text-white/50 mb-3">
                    O torneio já começou — você entrará na fila de espera e o organizador poderá te
                    colocar em uma dupla.
                  </p>
                )}
                <Button onClick={handleJoin} fullWidth isLoading={isJoining} size="md">
                  <LogIn className="w-5 h-5" />
                  {competition.requiresApproval
                    ? 'Pedir para entrar'
                    : competition.status === 'registration'
                      ? 'Entrar no torneio'
                      : 'Entrar na fila'}
                </Button>
              </>
            )}

            {joinError && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-coral text-sm mt-4">
                {joinError}
              </motion.p>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
